import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { ref, watch } from 'vue';

import { Loadable, Optional } from '@rygen/rygen-ui/model';

import api from '@/api';
import { Carrier } from '@/model';

import { useCurrentUserStore } from './CurrentUser';

type Scac = string;

type ScacToCarrierMap = { [scac: Scac]: Optional<Loadable<Carrier>> };
export interface PerformLoadOptions {
  bypassCache: boolean;
}

export interface CarrierStore {
  carriersByScac: Ref<ScacToCarrierMap>;
  loadCarrierByScac: (
    scac: Scac,
    opts?: PerformLoadOptions,
  ) => Loadable<Carrier>;
}

const PERFORM_LOAD_DEFAULT_OPTIONS: PerformLoadOptions = {
  bypassCache: false,
};

const carriersByScac: Ref<ScacToCarrierMap> = ref({});

const _useCarrierStore = defineStore('carrier', (): CarrierStore => {
  const { selectedDomainId } = useCurrentUserStore();

  const loadCarrierByScac = (
    scac: Scac,
    opts: PerformLoadOptions = PERFORM_LOAD_DEFAULT_OPTIONS,
  ): Loadable<Carrier> => {
    const options = { ...PERFORM_LOAD_DEFAULT_OPTIONS, ...opts };

    if (!carriersByScac.value[scac]) {
      carriersByScac.value[scac] = { loading: false };
    }

    const carrierLoadable = carriersByScac.value[scac];
    const requiresLoad = !carrierLoadable.data && !carrierLoadable.loading;

    return options.bypassCache || requiresLoad
      ? performLoad(scac)
      : carrierLoadable;
  };

  const performLoad = (scac: Scac): Loadable<Carrier> => {
    if (!carriersByScac.value[scac]) {
      carriersByScac.value[scac] = { loading: false };
    }

    const carrierLoadable = carriersByScac.value[scac];
    carrierLoadable.loading = true;

    api.carrier
      .findByScac(scac)
      .then((carrierResult) => {
        carrierLoadable.data = carrierResult;
        carrierLoadable.error = undefined;
      })
      .catch((e) => {
        if (axios.isCancel(e)) {
          return;
        }

        console.error('Error loading carrier', e);
        carrierLoadable.data = undefined;
        carrierLoadable.error = e;
      })
      .finally(() => {
        carrierLoadable.loading = false;
      });

    return carrierLoadable;
  };

  watch(selectedDomainId, () => {
    carriersByScac.value = {};
  });

  return {
    carriersByScac,
    loadCarrierByScac,
  };
});

export const useCarrierStore = (): CarrierStore => {
  const store = _useCarrierStore();
  const { carriersByScac } = storeToRefs(store);
  const { loadCarrierByScac } = store;

  return {
    carriersByScac,
    loadCarrierByScac,
  };
};
